// useFirebaseWithRateLimit.js
import { useRef, useState, useEffect, useCallback } from 'react';
import { useFirebase } from './useFirebase';
import { RateLimitManager } from './RateLimitManager';
import { getFirestore } from 'firebase/firestore';

export const OPERATION_TYPES = {
  READ: 'read',
  WRITE: 'write',
  DELETE: 'delete'
};

const RATE_LIMITS = {
  [OPERATION_TYPES.READ]: 300,
  [OPERATION_TYPES.WRITE]: 100,
  [OPERATION_TYPES.DELETE]: 50
};

export const useFirebaseWithRateLimit = () => {
  const firebase = useFirebase();
  const [rateLimitManager, setRateLimitManager] = useState(null);
  const [rateLimitError, setRateLimitError] = useState(null);
  const [operationCounts, setOperationCounts] = useState({});
  const [isRateLimited, setIsRateLimited] = useState(false);
  const timeoutRef = useRef(null);
  const windowStartRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Initialize rate limit manager
  useEffect(() => {
    if (firebase.user) {
      const db = getFirestore();
      const manager = new RateLimitManager(db, firebase.user.uid, RATE_LIMITS);
      setRateLimitManager(manager);
    } else {
      setRateLimitManager(null);
    }
  }, [firebase.user]);

  // Function to calculate and set timeout
  const setRateLimitTimeout = useCallback((windowStart) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const now = Date.now();
    const timeRemaining = Math.max(60000 - (now - windowStart), 0);

    timeoutRef.current = setTimeout(() => {
      setIsRateLimited(false);
      setRateLimitError(null);
      timeoutRef.current = null;
      windowStartRef.current = null;
    }, timeRemaining);

    windowStartRef.current = windowStart;
  }, []);

  const checkRateLimit = useCallback(async (operationType) => {
    if (!rateLimitManager) return true;
    if (isRateLimited) return false;

    try {
      await rateLimitManager.checkLimit(operationType);
      setRateLimitError(null);
      return true;
    } catch (error) {
      if (error.message.includes('Rate limit exceeded')) {
        setIsRateLimited(true);

        // Get the current window start time from the manager
        const counter = rateLimitManager.localCounters[operationType];
        const windowStart = counter?.windowStart || Date.now();

        // Calculate the full 60 seconds from window start
        const timeRemaining = Math.ceil((60000 - (Date.now() - windowStart)) / 1000);

        // Ensure we start with a full 60 seconds if the time calculation is off
        const adjustedTimeRemaining = Math.max(timeRemaining, 60);

        const errorMessage = `Rate limit exceeded for ${operationType} operations. ` +
          `Please wait ${adjustedTimeRemaining} seconds before trying again. ` +
          `Limit: ${RATE_LIMITS[operationType]} per minute.`;

        setRateLimitError(errorMessage);
        setRateLimitTimeout(windowStart);

        return false;
      }
      console.error('Rate limit check error:', error);
      throw error;
    }
  }, [rateLimitManager, isRateLimited, setRateLimitTimeout]);

  // Regular update of the error message with remaining time
  useEffect(() => {
    if (!isRateLimited || !windowStartRef.current) return;

    const updateInterval = setInterval(() => {
      const timeRemaining = Math.ceil((60000 - (Date.now() - windowStartRef.current)) / 1000);

      if (timeRemaining <= 0) {
        clearInterval(updateInterval);
        setIsRateLimited(false);
        setRateLimitError(null);
        windowStartRef.current = null;
        return;
      }

      const operationType = Object.keys(OPERATION_TYPES).find(type =>
        rateLimitManager?.localCounters[OPERATION_TYPES[type]]?.count >= RATE_LIMITS[OPERATION_TYPES[type]]
      );

      if (operationType) {
        setRateLimitError(`Rate limit exceeded for ${OPERATION_TYPES[operationType]} operations. ` +
          `Please wait ${timeRemaining} seconds before trying again. ` +
          `Limit: ${RATE_LIMITS[OPERATION_TYPES[operationType]]} per minute.`);
      }
    }, 1000);

    return () => clearInterval(updateInterval);
  }, [isRateLimited, rateLimitManager]);

  // Wrap operations with enforced rate limiting
  const wrapWithRateLimit = useCallback((operation, operationType) => {
    return async (...args) => {
      if (isRateLimited) {
        console.warn('Operation blocked: Rate limit currently exceeded');
        return null;
      }

      const canProceed = await checkRateLimit(operationType);
      if (!canProceed) {
        return null;
      }

      return operation(...args);
    };
  }, [checkRateLimit, isRateLimited]);

  // Apply rate limiting to all operations
  const addTask = useCallback(wrapWithRateLimit(firebase.addTask, OPERATION_TYPES.WRITE),
    [firebase.addTask, wrapWithRateLimit]);

  const updateTask = useCallback(wrapWithRateLimit(firebase.updateTask, OPERATION_TYPES.WRITE),
    [firebase.updateTask, wrapWithRateLimit]);

  const archiveTask = useCallback(wrapWithRateLimit(firebase.archiveTask, OPERATION_TYPES.DELETE),
    [firebase.archiveTask, wrapWithRateLimit]);

  const moveTask = useCallback(wrapWithRateLimit(firebase.moveTask, OPERATION_TYPES.WRITE),
    [firebase.moveTask, wrapWithRateLimit]);

  const updateQuadrantColors = useCallback(wrapWithRateLimit(firebase.updateQuadrantColors, OPERATION_TYPES.WRITE),
    [firebase.updateQuadrantColors, wrapWithRateLimit]);

  const updateUserPreferences = useCallback(wrapWithRateLimit(firebase.updateUserPreferences, OPERATION_TYPES.WRITE),
    [firebase.updateUserPreferences, wrapWithRateLimit]);

  const updateNoteOrder = useCallback(wrapWithRateLimit(firebase.updateNoteOrder, OPERATION_TYPES.WRITE),
    [firebase.updateNoteOrder, wrapWithRateLimit]);

  return {
    ...firebase,
    addTask,
    updateTask,
    archiveTask,
    moveTask,
    updateQuadrantColors,
    updateUserPreferences,
    updateNoteOrder,
    rateLimitError,
    operationCounts,
    rateLimits: RATE_LIMITS,
    isRateLimited
  };
};